import { ReferenceMeasure } from "@domain/project/floorplan/reference-measure";
import { FloorplanItemTextKonva } from "@project/floorplanner/konva/floorplan-item-text-konva";
import { FloorplanWorkspaceKonvaAdapter } from "@project/floorplanner/konva/floorplan-workspace-konva.adapter";
import { LabelLayoutKonva } from "@project/floorplanner/konva/label-layout-konva";
import Konva from "konva";
import { Group } from "konva/lib/Group";
import { Arrow } from "konva/lib/shapes/Arrow";
import { Line } from "konva/lib/shapes/Line";
import { Rect } from "konva/lib/shapes/Rect";
import { Text } from "konva/lib/shapes/Text";
export interface ReferenceMeasureData {
  width: number;
  height: number;
  rotation: number;
  x: number;
  y: number;
}

export class ReferenceMeasureKonva {
  static init(referenceMeasure: ReferenceMeasure): Group {
    const parentGroup = new Group();

    const measurementLineSpacing: number = 10;
    const arrowPointerSize: number = 10;

    const lineGroup = new Group({
      x: referenceMeasure.x,
      y: referenceMeasure.y,
      rotation: referenceMeasure.rotation,
      height: referenceMeasure.height,
      width: referenceMeasure.width,
      text: referenceMeasure.scale,
      draggable: false,
    });
    lineGroup.setAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY, referenceMeasure);
    lineGroup.setAttr("minWidth", 80);
    lineGroup.setAttr("minHeight", 30);
    const rect = new Rect({
      x: 0,
      width: referenceMeasure.width,
      height: referenceMeasure.height,
    });
    const arrow = new Arrow({
      points: [0, 0, referenceMeasure.width, 0],
      pointerLength: arrowPointerSize,
      pointerWidth: arrowPointerSize,
      fill: "black",
      stroke: "black",
      strokeWidth: 2,
      pointerAtBeginning: true,
    });
    const lineLeft = new Line({
      points: [-2, -referenceMeasure.height / 2, -2, referenceMeasure.height / 2],
      stroke: "black",
      strokeWidth: 2,
    });
    const lineRight = new Line({
      points: [referenceMeasure.width + 2, -referenceMeasure.height / 2, referenceMeasure.width + 2, referenceMeasure.height / 2],
      stroke: "black",
      strokeWidth: 2,
    });
    const referenceMeasureText = new Text({
      text: referenceMeasure.scale.toString() + " Meter",
      fontSize: 15,
    });

    const itemText = FloorplanItemTextKonva.init(referenceMeasure);
    const text = FloorplanItemTextKonva.getText(itemText);

    text.fontSize(15);
    FloorplanItemTextKonva.updateBackground(itemText);
    referenceMeasureText.x((referenceMeasure.width - referenceMeasureText.width()) / 2);

    arrow.y(rect.height() / 2);

    lineLeft.y(rect.height() / 2);
    lineRight.y(rect.height() / 2);
    lineGroup.add(referenceMeasureText);

    lineGroup.add(rect);
    lineGroup.add(arrow);
    lineGroup.add(lineLeft);
    lineGroup.add(lineRight);

    const measurementLines: Konva.Line[] = this.createMeasurementLines(measurementLineSpacing, lineGroup, arrowPointerSize);

    lineGroup.setAttr("measurementLines", measurementLines);
    lineGroup.setAttr("arrowPointerLength", arrowPointerSize);
    lineGroup.setAttr("measurementLineSpacing", measurementLineSpacing);

    parentGroup.add(lineGroup, itemText);
    this.setTextPosition(itemText, lineGroup);

    let currentData: ReferenceMeasureData;
    let pendingData: ReferenceMeasureData;

    lineGroup.on("transformstart", () => {
      itemText.hide();
      currentData = {
        width: lineGroup.width(),
        height: lineGroup.height(),
        rotation: lineGroup.rotation(),
        x: lineGroup.x(),
        y: lineGroup.y(),
      };
    });

    lineGroup.on("transform", () => {
      this.refreshReferenceMeasure(lineGroup);
    });

    lineGroup.on("transformend", () => {
      pendingData = {
        width: lineGroup.width(),
        height: lineGroup.height(),
        rotation: lineGroup.rotation(),
        x: lineGroup.x(),
        y: lineGroup.y(),
      };
      referenceMeasure.lengthChangedKonva(currentData, pendingData);

      this.scaleText(text, lineGroup);
      FloorplanItemTextKonva.updateBackground(itemText);
      this.setTextPosition(itemText, lineGroup);

      const labelHidden = itemText.getAttr("labelHidden") === true;
      if (!labelHidden) {
        itemText.show();
      }
    });

    lineGroup.on("dragmove", () => {
      this.setTextPosition(itemText, lineGroup);
    });

    parentGroup.add(lineGroup, itemText);

    return parentGroup;
  }

  static resetReferenceMeasure(group: Konva.Group, currentData: ReferenceMeasureData, floorplanItem: ReferenceMeasure) {
    const lineGroup = group.getChildren()[0] as Konva.Group;
    lineGroup.width(currentData.width);
    lineGroup.height(currentData.height);
    lineGroup.rotation(currentData.rotation);
    lineGroup.x(currentData.x);
    lineGroup.y(currentData.y);

    this.refreshReferenceMeasure(lineGroup);
    this.refreshItemText(floorplanItem, group);
  }

  static refreshReferenceMeasure(lineGroup: Konva.Group) {
    lineGroup.width(lineGroup.width() * lineGroup.scaleX());
    lineGroup.height(lineGroup.height() * lineGroup.scaleY());

    const referenceMeasureText = lineGroup.getChildren()[0] as Konva.Text;
    const rect = lineGroup.getChildren()[1] as Konva.Rect;
    const arrow = lineGroup.getChildren()[2] as Konva.Arrow;
    const lineLeft = lineGroup.getChildren()[3] as Konva.Line;
    const lineRight = lineGroup.getChildren()[4] as Konva.Line;

    rect.y(0);
    rect.height(lineGroup.height());
    rect.width(lineGroup.width());

    arrow.points([0, 0, lineGroup.width(), 0]);
    arrow.y(rect.height() / 2);

    lineLeft.points([-2, -lineGroup.height() / 2, -2, lineGroup.height() / 2]);
    lineLeft.y(rect.height() / 2);
    lineRight.points([lineGroup.width() + 2, -lineGroup.height() / 2, lineGroup.width() + 2, lineGroup.height() / 2]);
    lineRight.y(rect.height() / 2);

    this.scaleText(referenceMeasureText, lineGroup);
    referenceMeasureText.x((lineGroup.width() - referenceMeasureText.width()) / 2);

    lineGroup.scaleX(1);
    lineGroup.scaleY(1);

    const measurementLines = lineGroup.getAttr("measurementLines") as Konva.Line[];
    const arrowPointerLength = lineGroup.getAttr("arrowPointerLength") as number;
    const measurementLineSpacing = lineGroup.getAttr("measurementLineSpacing") as number;

    if (measurementLines && arrowPointerLength && measurementLineSpacing) {
      this.updateMeasurementLines(measurementLines, lineGroup, arrowPointerLength, measurementLineSpacing);
    }
  }

  static refreshReferenceMeasureText(referenceMeasure: ReferenceMeasure, group: Group) {
    const referenceMeasureLineGroup = group.getChildren()[0] as Konva.Group;
    const text = referenceMeasureLineGroup.getChildren()[0] as Konva.Text;
    text.text(referenceMeasure.scale.toString() + " Meter");
    text.x((referenceMeasure.width - text.width()) / 2);
  }

  static refreshItemText(referenceMeasure: ReferenceMeasure, group: Group) {
    const itemText = group.getChildren()[1] as Konva.Group;
    const lineGroup = group.getChildren()[0] as Konva.Group;
    FloorplanItemTextKonva.refreshText(referenceMeasure, itemText);
    const text = FloorplanItemTextKonva.getText(itemText);
    this.scaleText(text, lineGroup);
    FloorplanItemTextKonva.updateBackground(itemText);
    this.setTextPosition(itemText, lineGroup);
  }

  private static setTextPosition(itemText: Group, lineGroup: Group) {
    itemText.offsetY(0);
    itemText.offsetX(0);

    const item = lineGroup.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY);
    LabelLayoutKonva.placeLabelForItem(itemText, lineGroup, item);
  }

  private static createMeasurementLines(numberOfMeasurementLines: number, lineGroup: Konva.Group, arrowPointerLength: number) {
    const lines: Konva.Line[] = [];
    const spaceBetweenLines = (lineGroup.width() - arrowPointerLength * 2) / (numberOfMeasurementLines + 1);
    const lineHeight = 8;

    for (let i = 1; i <= numberOfMeasurementLines; i++) {
      const x = spaceBetweenLines * i + arrowPointerLength;
      const measurementLine = new Line({
        points: [x, lineGroup.height() / 2 - lineHeight / 2, x, lineGroup.height() / 2 + lineHeight / 2],
        stroke: "black",
        strokeWidth: 2,
      });
      lines.push(measurementLine);
      lineGroup.add(measurementLine);
    }
    return lines;
  }

  private static updateMeasurementLines(
    measurementlines: Konva.Line[],
    lineGroup: Konva.Group,
    arrowPointerLength: number,
    numberOfMeasurementLines: number,
  ) {
    const spaceBetweenLines = (lineGroup.width() - arrowPointerLength * 2) / (numberOfMeasurementLines + 1);
    const lineHeight = 8;

    for (let i = 1; i <= numberOfMeasurementLines; i++) {
      const x = spaceBetweenLines * i + arrowPointerLength;

      measurementlines[i - 1].points([x, lineGroup.height() / 2 - lineHeight / 2, x, lineGroup.height() / 2 + lineHeight / 2]);
    }
  }

  private static scaleText(itemText: Text, measureGroup: Group) {
    itemText.fontSize(measureGroup.height() / 3);
  }
}
